import { Injectable } from '@angular/core';
import {
  BehaviorSubject,
  Observable,
  of,
  throwError,
  timer,
} from 'rxjs';
import {
  catchError,
  finalize,
  last,
  map,
  switchMap,
  takeWhile,
} from 'rxjs/operators';
import { GraphService } from 'src/app/services/api/graph.service';
import {
  CREATE_ORDER_JOB_POLL_INTERVAL_MILLISECONDS,
  CREATE_ORDER_JOB_TIMEOUT_MILLISECONDS,
  ConfirmPreliminaryOrderResult,
  CreateOrderInput,
  CreateOrderJobResult,
  CreateOrderJobStatus,
  isCreateOrderJobInProgress,
} from '../model/create-order.model';
import { OrderRecalcHistoryItem } from '../model/order.model';
import {
  OrderPdfDownloadUrlResult,
  RetryGenerateOrderPdfResult,
} from '../model/order-pdf.model';
import {
  DeleteOrderInput,
  UpdateOrderReservationNumberInput,
  UpdateOrderReservationStatusInput,
  UpdateOrderResult,
  UpdateOrderScheduledPaymentDateInput,
} from '../model/update-order.model';
import {
  CONFIRM_PRELIMINARY_ORDER_MUTATION,
  CREATE_ORDER_MUTATION,
  CREATE_ORDER_PDF_DOWNLOAD_URL_MUTATION,
  DELETE_ORDER_MUTATION,
  GET_CREATE_ORDER_JOB_QUERY,
  GET_ORDER_RECALC_HISTORY_QUERY,
  RETRY_GENERATE_ORDER_PDF_MUTATION,
  UPDATE_ORDER_FREEZE_DEADLINE_MUTATION,
  UPDATE_ORDER_RESERVATION_NUMBER_MUTATION,
  UPDATE_ORDER_RESERVATION_STATUS_MUTATION,
  UPDATE_ORDER_SCHEDULED_PAYMENT_DATE_MUTATION,
} from './order.mutations';
import {
  createGraphqlClientError,
  logOrderDevDetail,
} from '../lib/graphql-error.utils';

/**
 * Service managing GraphQL operations and asynchronous workflows for Órdenes de Servicio (Orders).
 */
@Injectable({ providedIn: 'root' })
export class OrderService {
  private readonly isCreateOrderBusySubject: BehaviorSubject<boolean> =
    new BehaviorSubject<boolean>(false);

  public readonly isCreateOrderBusy$: Observable<boolean> =
    this.isCreateOrderBusySubject.asObservable();

  /**
   * Initializes the OrderService with injected GraphService.
   *
   * Description:
   *   Injects graph service client for Core GraphQL endpoint.
   *
   * Parameters:
   *   graphService: GraphService - GraphQL client service.
   *
   * Return Value:
   *   OrderService - Initialized service instance.
   *
   * Exceptions & Errors:
   *   None.
   */
  constructor(private readonly graphService: GraphService) {}

  /**
   * Evaluates whether an order creation job is actively in progress.
   *
   * Description:
   *   Returns the current boolean value of isCreateOrderBusySubject.
   *
   * Parameters:
   *   None.
   *
   * Return Value:
   *   boolean - True if busy; false otherwise.
   *
   * Exceptions & Errors:
   *   None.
   */
  public get isCreateOrderBusy(): boolean {
    return this.isCreateOrderBusySubject.value;
  }

  /**
   * Submits createOrder mutation to enqueue the order creation job in the backend.
   *
   * Description:
   *   Dispatches mutation payload to Core GraphQL endpoint.
   *
   * Parameters:
   *   creationPayloadInput: CreateOrderInput - Payload containing items and optional metadata.
   *
   * Return Value:
   *   Observable<CreateOrderJobResult> - Enqueued job details.
   *
   * Exceptions & Errors:
   *   Emits GraphQLClientError on network or schema failures.
   */
  public createOrder(
    creationPayloadInput: CreateOrderInput,
  ): Observable<CreateOrderJobResult> {
    return this.graphService
      .mutateGraphQLCore<{ createOrder: CreateOrderJobResult }>({
        mutation: CREATE_ORDER_MUTATION,
        variables: { input: creationPayloadInput },
      })
      .pipe(
        map((responsePayload: any) =>
          this.normalizeJobPayload(responsePayload?.createOrder),
        ),
      );
  }

  /** Historial de recálculos de monto y pasajeros de una OS. */
  public getOrderRecalcHistory(
    orderId: string,
  ): Observable<OrderRecalcHistoryItem[]> {
    return this.graphService
      .queryGraphQLCore<{ getOrderRecalcHistory: OrderRecalcHistoryItem[] }>({
        query: GET_ORDER_RECALC_HISTORY_QUERY,
        variables: { orderId },
      })
      .pipe(
        map(
          (responsePayload: any) =>
            responsePayload?.getOrderRecalcHistory ?? [],
        ),
      );
  }

  /** Oficializa una OS PRELIMINAR (recalcula, congela costo y dispara PDF). */
  public confirmPreliminaryOrder(
    orderId: string,
  ): Observable<ConfirmPreliminaryOrderResult> {
    return this.graphService
      .mutateGraphQLCore<{
        confirmPreliminaryOrder: ConfirmPreliminaryOrderResult;
      }>({
        mutation: CONFIRM_PRELIMINARY_ORDER_MUTATION,
        variables: { orderId },
      })
      .pipe(
        map(
          (responsePayload: any) =>
            responsePayload?.confirmPreliminaryOrder,
        ),
      );
  }

  /** Actualiza el plazo «Confirmar antes del» de una OS preliminar. */
  public updateOrderFreezeDeadline(
    orderId: string,
    freezeDeadlineAt: string,
  ): Observable<ConfirmPreliminaryOrderResult> {
    return this.graphService
      .mutateGraphQLCore<{
        updateOrderFreezeDeadline: ConfirmPreliminaryOrderResult;
      }>({
        mutation: UPDATE_ORDER_FREEZE_DEADLINE_MUTATION,
        variables: { orderId, freezeDeadlineAt },
      })
      .pipe(
        map(
          (responsePayload: any) =>
            responsePayload?.updateOrderFreezeDeadline,
        ),
      );
  }

  /**
   * Queries status and result of a createOrder job by its unique jobId.
   *
   * Description:
   *   Executes getCreateOrderJob GraphQL query.
   *
   * Parameters:
   *   jobIdentifierString: string - The unique job ID.
   *
   * Return Value:
   *   Observable<CreateOrderJobResult> - Current job status and results.
   *
   * Exceptions & Errors:
   *   Throws GraphQLClientError if backend returns errors.
   */
  public getCreateOrderJob(
    jobIdentifierString: string,
  ): Observable<CreateOrderJobResult> {
    return this.graphService
      .queryGraphQLCore<{ getCreateOrderJob: CreateOrderJobResult } | {
        data?: { getCreateOrderJob: CreateOrderJobResult };
        errors?: Array<{ message: string; extensions?: { code?: string } }>;
      }>({
        query: GET_CREATE_ORDER_JOB_QUERY,
        variables: { jobId: jobIdentifierString },
      })
      .pipe(
        map((queryResponsePayload: any) => {
          if (queryResponsePayload?.errors?.length) {
            throw createGraphqlClientError(queryResponsePayload.errors);
          }

          const rawJobRecord =
            queryResponsePayload?.getCreateOrderJob ??
            queryResponsePayload?.data?.getCreateOrderJob;

          return this.normalizeJobPayload(rawJobRecord);
        }),
      );
  }

  /**
   * Submits createOrder mutation and polls getCreateOrderJob until COMPLETED, FAILED, or timeout.
   *
   * Description:
   *   Enqueues job, marks service busy, polls periodically, and finalizes state.
   *
   * Parameters:
   *   creationPayloadInput: CreateOrderInput - Order creation input payload.
   *
   * Return Value:
   *   Observable<CreateOrderJobResult> - Final completed job record.
   *
   * Exceptions & Errors:
   *   Throws Error when job fails or exceeds safety timeout.
   */
  public createOrderWithPolling(
    creationPayloadInput: CreateOrderInput,
  ): Observable<CreateOrderJobResult> {
    const jobStartTimeTimestamp: number = Date.now();
    this.isCreateOrderBusySubject.next(true);

    return this.createOrder(creationPayloadInput).pipe(
      switchMap((initialJobRecord: CreateOrderJobResult) =>
        this.waitForCreateOrderJobCompletion(
          initialJobRecord,
          jobStartTimeTimestamp,
        ),
      ),
      finalize(() => this.isCreateOrderBusySubject.next(false)),
    );
  }

  /**
   * Logically deletes an Order of Service (sets status to ELIMINADO and frees service links).
   *
   * Description:
   *   Dispatches deleteOrder mutation with target orderId.
   *
   * Parameters:
   *   deletionPayloadInput: DeleteOrderInput - Payload containing orderId and optional reason.
   *
   * Return Value:
   *   Observable<UpdateOrderResult> - Updated order metadata.
   *
   * Exceptions & Errors:
   *   Throws GraphQLClientError if order cannot be deleted.
   */
  public deleteOrder(
    deletionPayloadInput: DeleteOrderInput,
  ): Observable<UpdateOrderResult> {
    return this.executeOrderUpdateMutation(
      DELETE_ORDER_MUTATION,
      deletionPayloadInput,
      'deleteOrder',
    );
  }

  /**
   * Requests a presigned URL to view (inline) or download (attachment) the Order PDF.
   *
   * Description:
   *   Executes createOrderPdfDownloadUrl mutation against Core GraphQL endpoint.
   *
   * Parameters:
   *   orderIdentifierString: string - The ID of the target order.
   *   deliveryDisposition: 'inline' | 'attachment' | null - Delivery mode.
   *
   * Return Value:
   *   Observable<OrderPdfDownloadUrlResult> - Presigned URL and PDF status.
   *
   * Exceptions & Errors:
   *   Throws GraphQLClientError on network failure.
   */
  public createOrderPdfDownloadUrl(
    orderIdentifierString: string,
    deliveryDisposition?: 'inline' | 'attachment' | null,
  ): Observable<OrderPdfDownloadUrlResult> {
    const mutationVariables: { orderId: string; disposition?: string } = {
      orderId: orderIdentifierString,
    };
    if (deliveryDisposition) {
      mutationVariables.disposition = deliveryDisposition;
    }

    return this.graphService
      .mutateGraphQLCore<{
        createOrderPdfDownloadUrl: OrderPdfDownloadUrlResult;
      }>({
        mutation: CREATE_ORDER_PDF_DOWNLOAD_URL_MUTATION,
        variables: mutationVariables,
      })
      .pipe(
        map(
          (responsePayload: any) =>
            responsePayload?.createOrderPdfDownloadUrl,
        ),
      );
  }

  /**
   * Requests a retry for PDF generation when in FAILED status.
   *
   * Description:
   *   Dispatches retryGenerateOrderPdf mutation.
   *
   * Parameters:
   *   orderIdentifierString: string - Target order ID.
   *
   * Return Value:
   *   Observable<RetryGenerateOrderPdfResult> - Updated status response.
   *
   * Exceptions & Errors:
   *   Throws GraphQLClientError if retry is rejected by backend.
   */
  public retryGenerateOrderPdf(
    orderIdentifierString: string,
  ): Observable<RetryGenerateOrderPdfResult> {
    return this.graphService
      .mutateGraphQLCore<{
        retryGenerateOrderPdf: RetryGenerateOrderPdfResult;
      }>({
        mutation: RETRY_GENERATE_ORDER_PDF_MUTATION,
        variables: { orderId: orderIdentifierString },
      })
      .pipe(
        map(
          (responsePayload: any) => responsePayload?.retryGenerateOrderPdf,
        ),
      );
  }

  /**
   * Updates scheduled payment date for an order in CONFIRMADO status.
   *
   * Description:
   *   Executes updateOrderScheduledPaymentDate mutation.
   *
   * Parameters:
   *   updatePayloadInput: UpdateOrderScheduledPaymentDateInput - Update payload.
   *
   * Return Value:
   *   Observable<UpdateOrderResult> - Updated order metadata.
   *
   * Exceptions & Errors:
   *   Throws GraphQLClientError on failure.
   */
  public updateOrderScheduledPaymentDate(
    updatePayloadInput: UpdateOrderScheduledPaymentDateInput,
  ): Observable<UpdateOrderResult> {
    return this.executeOrderUpdateMutation(
      UPDATE_ORDER_SCHEDULED_PAYMENT_DATE_MUTATION,
      updatePayloadInput,
      'updateOrderScheduledPaymentDate',
    );
  }

  /**
   * Updates reservation number on an order in CONFIRMADO status.
   *
   * Description:
   *   Executes updateOrderReservationNumber mutation.
   *
   * Parameters:
   *   updatePayloadInput: UpdateOrderReservationNumberInput - Update payload.
   *
   * Return Value:
   *   Observable<UpdateOrderResult> - Updated order metadata.
   *
   * Exceptions & Errors:
   *   Throws GraphQLClientError on failure.
   */
  public updateOrderReservationNumber(
    updatePayloadInput: UpdateOrderReservationNumberInput,
  ): Observable<UpdateOrderResult> {
    return this.executeOrderUpdateMutation(
      UPDATE_ORDER_RESERVATION_NUMBER_MUTATION,
      updatePayloadInput,
      'updateOrderReservationNumber',
    );
  }

  /**
   * Updates reservation status on an order in CONFIRMADO status.
   *
   * Description:
   *   Executes updateOrderReservationStatus mutation.
   *
   * Parameters:
   *   updatePayloadInput: UpdateOrderReservationStatusInput - Update payload.
   *
   * Return Value:
   *   Observable<UpdateOrderResult> - Updated order metadata.
   *
   * Exceptions & Errors:
   *   Throws GraphQLClientError on failure.
   */
  public updateOrderReservationStatus(
    updatePayloadInput: UpdateOrderReservationStatusInput,
  ): Observable<UpdateOrderResult> {
    return this.executeOrderUpdateMutation(
      UPDATE_ORDER_RESERVATION_STATUS_MUTATION,
      updatePayloadInput,
      'updateOrderReservationStatus',
    );
  }

  /**
   * Internal polling orchestrator checking job status until terminal state or timeout.
   *
   * Description:
   *   Uses RxJS timer to query getCreateOrderJob periodically.
   *
   * Parameters:
   *   initialJobRecord: CreateOrderJobResult - Initial job payload from mutation.
   *   jobStartTimeTimestamp: number - Millisecond epoch timestamp when polling began.
   *
   * Return Value:
   *   Observable<CreateOrderJobResult> - Completed job record.
   *
   * Exceptions & Errors:
   *   Throws Error when status is FAILED or timeout expires.
   */
  private waitForCreateOrderJobCompletion(
    initialJobRecord: CreateOrderJobResult,
    jobStartTimeTimestamp: number,
  ): Observable<CreateOrderJobResult> {
    const handleTerminalOrThrow = (
      currentJobRecord: CreateOrderJobResult,
    ): Observable<CreateOrderJobResult> => {
      const normalizedStatus =
        currentJobRecord.status?.trim().toUpperCase();

      if (normalizedStatus === CreateOrderJobStatus.COMPLETED) {
        return of(currentJobRecord);
      }

      if (normalizedStatus === CreateOrderJobStatus.FAILED) {
        logOrderDevDetail('createOrder.lastError', currentJobRecord.lastError);
        return throwError(() => ({
          message:
            currentJobRecord.lastError ||
            'La creación de órdenes de servicio falló en el servidor.',
          graphQLErrors: [
            {
              message:
                currentJobRecord.lastError || 'CREATE_ORDER_JOB_FAILED',
              extensions: {
                code:
                  currentJobRecord.errorCode || 'CREATE_ORDER_JOB_FAILED',
                userMessage: currentJobRecord.userMessage,
              },
            },
          ],
        }));
      }

      if (
        Date.now() - jobStartTimeTimestamp >
        CREATE_ORDER_JOB_TIMEOUT_MILLISECONDS
      ) {
        return throwError(() => ({
          message:
            'Tiempo de espera agotado al procesar la creación de órdenes.',
          graphQLErrors: [
            {
              message: 'TIMEOUT_CREATE_ORDER_JOB',
              extensions: { code: 'TIMEOUT_CREATE_ORDER_JOB' },
            },
          ],
        }));
      }

      return of(currentJobRecord);
    };

    if (!isCreateOrderJobInProgress(initialJobRecord?.status)) {
      return handleTerminalOrThrow(initialJobRecord);
    }

    return timer(0, CREATE_ORDER_JOB_POLL_INTERVAL_MILLISECONDS).pipe(
      switchMap(() => {
        if (
          Date.now() - jobStartTimeTimestamp >
          CREATE_ORDER_JOB_TIMEOUT_MILLISECONDS
        ) {
          return throwError(() => ({
            message:
              'Tiempo de espera agotado al procesar la creación de órdenes.',
            graphQLErrors: [
              {
                message: 'TIMEOUT_CREATE_ORDER_JOB',
                extensions: { code: 'TIMEOUT_CREATE_ORDER_JOB' },
              },
            ],
          }));
        }
        return this.getCreateOrderJob(initialJobRecord.jobId);
      }),
      takeWhile(
        (polledJobRecord: CreateOrderJobResult) =>
          isCreateOrderJobInProgress(polledJobRecord?.status),
        true,
      ),
      last(),
      switchMap((lastPolledJobRecord: CreateOrderJobResult) =>
        handleTerminalOrThrow(lastPolledJobRecord),
      ),
      catchError((error) => throwError(() => error)),
    );
  }

  /**
   * Helper executing generic Order update mutations.
   *
   * Description:
   *   Mutates GraphQL Core and extracts named root property.
   *
   * Parameters:
   *   mutationGraphQLQueryString: string - The mutation query string.
   *   inputVariablesPayload: unknown - Variables object.
   *   responseRootKeyName: string - Field name to unwrap from response.
   *
   * Return Value:
   *   Observable<UpdateOrderResult> - Response data.
   *
   * Exceptions & Errors:
   *   Throws GraphQLClientError on mutation failure.
   */
  private executeOrderUpdateMutation(
    mutationGraphQLQueryString: string,
    inputVariablesPayload: unknown,
    responseRootKeyName: string,
  ): Observable<UpdateOrderResult> {
    return this.graphService
      .mutateGraphQLCore<{ [key: string]: UpdateOrderResult }>({
        mutation: mutationGraphQLQueryString,
        variables: { input: inputVariablesPayload },
      })
      .pipe(
        map(
          (responsePayload: any) =>
            responsePayload?.[responseRootKeyName],
        ),
      );
  }

  /**
   * Normalizes raw job payload ensuring default arrays.
   *
   * Description:
   *   Provides empty arrays for createdOrders, rejectedServices, and warnings if undefined.
   *
   * Parameters:
   *   rawJobPayload: CreateOrderJobResult | null | undefined - Raw response object.
   *
   * Return Value:
   *   CreateOrderJobResult - Normalized payload.
   *
   * Exceptions & Errors:
   *   None.
   */
  private normalizeJobPayload(
    rawJobPayload: CreateOrderJobResult | null | undefined,
  ): CreateOrderJobResult {
    return {
      jobId: rawJobPayload?.jobId || '',
      status: rawJobPayload?.status || CreateOrderJobStatus.PENDING,
      errorCode: rawJobPayload?.errorCode,
      lastError: rawJobPayload?.lastError,
      userMessage: rawJobPayload?.userMessage,
      createdOrders: rawJobPayload?.createdOrders || [],
      rejectedServices: rawJobPayload?.rejectedServices || [],
      warnings: rawJobPayload?.warnings || [],
    };
  }
}
